"use client";

import { Compass, Box, Leaf, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "§ 01.1",
    icon: Compass,
    title: "Protection",
    italicWord: "Island",
    titleSuffix: "",
    description: "A vital sanctuary for nesting seabirds and harbor seals, requiring strict public awareness to maintain its fragile, undisturbed ecosystem."
  }, {
    icon: Box,
    index: "§ 01.2",
    title: "Dungeness",
    italicWord: "Spit",
    titleSuffix: "",
    description: "One of the world's longest natural sand spits, providing essential habitat for migratory birds and diverse marine life along the coast."
  }, {
    icon: Leaf,
    index: "§ 01.3",
    title: "Wildlife",
    italicWord: "Advocacy",
    titleSuffix: "",
    description: "Empowering the public through education to ensure these protected lands remain safe havens for generations of wildlife to come."
  }];
  return <section data-section-id="2110" id="the-refuges-we-protect" className="dark relative overflow-hidden bg-[#0F3D2E] py-28 md:py-36" style={{
    color: "#F7FBFF"
  }}>
      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #F7FBFF",
      opacity: 0.06,
      fontWeight: 350
    }}>
        01
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px #F7FBFF",
      opacity: 0.06,
      fontWeight: 350
    }}>
        01
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            {}
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.6875rem",
            fontWeight: 500,
            letterSpacing: "0.18em",
            lineHeight: 1
          }}>
              <span style={{
              color: "#0000FF"
            }}>§ 01</span>
              <span style={{
              color: "#228B22"
            }}>—</span>
              <span style={{
              color: "#EAF4EA"
            }}>REFUGES / OUR MISSION</span>
            </div>

            {}
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1" style={{
              backgroundColor: "#228B22"
            }} />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "#0000FF"
              }} />
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "#0000FF"
              }} />
                <span className="h-1 w-1 rounded-lg" style={{
                backgroundColor: "#0000FF"
              }} />
              </div>
            </div>

            {}
            <h2 className="mt-8 font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.02,
            color: "#F7FBFF"
          }}>
              Protecting our{" "}
              <br className="hidden sm:block" />
              wildlife{" "}
              <em style={{
              fontStyle: "italic",
              fontWeight: 400
            }}>
                sanctuaries
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md" style={{
            fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
            fontWeight: 400,
            lineHeight: 1.6,
            color: "#EAF4EA"
          }}>
              Friends of the Refuges informs the public on how to protect our wildlife and sea bird refuges, ensuring the long-term health of these vital coastal ecosystems.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-12 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`lg:col-span-4 transition-all ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-9 transition-transform duration-300 ease-out hover:-translate-y-1" style={{
              backgroundColor: "#EAF4EA",
              color: "#0F3D2E",
              borderRadius: "4px",
              border: "none"
            }}>
                  {}
                  <span aria-hidden="true" className="pointer-events-none absolute left-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderTop: "1px solid #0000FF",
                borderLeft: "1px solid #0000FF"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute right-3 top-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderTop: "1px solid #0000FF",
                borderRight: "1px solid #0000FF"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 left-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderBottom: "1px solid #0000FF",
                borderLeft: "1px solid #0000FF"
              }} />
                  <span aria-hidden="true" className="pointer-events-none absolute bottom-3 right-3 h-3.5 w-3.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100" style={{
                borderBottom: "1px solid #0000FF",
                borderRight: "1px solid #0000FF"
              }} />

                  {}
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.6875rem",
                fontWeight: 500,
                letterSpacing: "0.18em",
                lineHeight: 1
              }}>
                    <span style={{
                  color: "#0000FF"
                }}>{pillar.index}</span>
                    <span style={{
                  color: "#228B22"
                }}>—</span>
                    <span style={{
                  color: "#0F3D2E"
                }}>FOCUS</span>
                  </div>

                  {}
                  <div className="mt-8 transition-transform duration-[400ms] ease-out group-hover:rotate-6">
                    <Icon size={32} strokeWidth={1.5} style={{
                  color: "#228B22"
                }} />
                  </div>

                  {}
                  <h3 className="mt-7 font-default" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                fontWeight: 600,
                letterSpacing: "0",
                lineHeight: 1.3,
                color: "#0F3D2E"
              }}>
                    {pillar.title}{" "}
                    <em style={{
                  fontStyle: "italic",
                  fontWeight: 400
                }}>
                      {pillar.italicWord}
                    </em>
                  </h3>

                  {}
                  <p className="font-default mt-5" style={{
                fontSize: "1.125rem",
                fontWeight: 400,
                letterSpacing: "0.01em",
                lineHeight: 1.6,
                color: "#0F3D2E"
              }}>
                    {pillar.description}
                  </p>

                  {}
                  <div className="mt-9 flex items-center gap-3">
                    <div className="h-px flex-1" style={{
                  backgroundColor: "#228B22"
                }} />
                    <div className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "#0000FF"
                  }} />
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "#0000FF"
                  }} />
                      <span className="h-1 w-1 rounded-lg" style={{
                    backgroundColor: "#0000FF"
                  }} />
                    </div>
                  </div>

                  {}
                  <div className="mt-6">
                    <button type="button" className="group/link relative inline-flex items-center gap-2 font-default" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  color: "#0000FF"
                }}>
                      <span className="relative">
                        Learn more
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full" style={{
                      backgroundColor: "#0000FF"
                    }} />
                      </span>
                      <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </button>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}